import React from 'react';
import { motion } from 'framer-motion';
import { Link, useParams, Navigate } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, ArrowRight, ExternalLink, Check } from 'lucide-react';
import { caseStudiesData } from '../mockData';
import { Button } from '../components/ui/button';
import { useSEO } from '../hooks/useSEO';
import JsonLd from '../components/JsonLd';

const CaseStudy = () => {
  const { slug } = useParams();
  const study = caseStudiesData.find((s) => s.slug === slug);

  useSEO({
    title: study
      ? `${study.title} — Case Study | Hei Web Creations Singapore`
      : 'Case Study | Hei Web Creations',
    description: study
      ? `${study.tagline} ${study.services.join(' + ')} delivered as a S$3,999 Growth Bundle.`.slice(0, 158)
      : undefined,
    image: study?.heroImage,
    type: 'article',
  });

  if (!study) return <Navigate to="/" replace />;

  const origin = typeof window !== 'undefined' ? window.location.origin : '';
  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: `${origin}/` },
      { '@type': 'ListItem', position: 2, name: 'Case Studies', item: `${origin}/case-studies` },
      { '@type': 'ListItem', position: 3, name: study.title, item: `${origin}/case-studies/${study.slug}` },
    ],
  };
  const caseStudySchema = {
    '@context': 'https://schema.org',
    '@type': 'CreativeWork',
    name: `${study.title} — Case Study`,
    headline: study.tagline,
    about: study.industry,
    keywords: study.keywords.join(', '),
    author: { '@type': 'Organization', name: 'Hei Web Creations Pte Ltd' },
    url: `${origin}/case-studies/${study.slug}`,
  };

  const otherStudies = caseStudiesData.filter((s) => s.slug !== slug).slice(0, 3);

  return (
    <article className="pt-32 bg-white" data-testid={`case-study-${study.slug}`}>
      <JsonLd id="breadcrumb-case" data={breadcrumbSchema} />
      <JsonLd id="case-schema" data={caseStudySchema} />

      {/* Header */}
      <section className="py-12 border-b border-gray-100">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto">
            <Link
              to="/"
              className="inline-flex items-center font-mono text-sm text-gray-500 hover:text-[#C8102E] transition-colors mb-8"
              data-testid="back-home-link"
            >
              <ArrowLeft size={16} className="mr-2" />
              cd ../case-studies
            </Link>

            <div className="flex flex-wrap items-center gap-3 mb-6 font-mono text-xs">
              <span className="text-[#C8102E] uppercase tracking-wider">{study.industry}</span>
              <span className="text-gray-300">/</span>
              <span className="text-gray-400">{study.location}</span>
              <span className="text-gray-300">/</span>
              <span className="text-gray-400">{study.year}</span>
            </div>

            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#1A1A1A] leading-[0.98] tracking-tight mb-5"
              data-testid="case-title"
            >
              {study.title}
            </motion.h1>
            <p className="text-xl md:text-2xl text-gray-500 leading-relaxed max-w-3xl">
              {study.tagline}
            </p>

            <a
              href={study.liveUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 mt-8 text-sm font-mono text-[#1A1A1A] hover:text-[#C8102E] transition-colors border-b border-gray-200 hover:border-[#C8102E] pb-1"
              data-testid="case-live-link"
            >
              Visit live site <ExternalLink size={14} />
            </a>
          </div>
        </div>
      </section>

      {/* Hero Image */}
      <div className="container mx-auto px-6 py-12">
        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
          className="max-w-6xl mx-auto"
        >
          <div className="rounded-3xl overflow-hidden aspect-[16/9] bg-[#FAFAFA]">
            <img
              src={study.heroImage}
              alt={`${study.title} — case study hero image by Hei Web Creations`}
              className="w-full h-full object-cover"
              loading="eager"
            />
          </div>
        </motion.div>
      </div>

      {/* Package callout */}
      <section className="py-12 bg-[#FAFAFA] border-y border-gray-100">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.28em] text-[#C8102E] mb-3 block">
                ~/package
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] tracking-tight leading-[1.05]">
                Logo · Website · SEO · Social — all in one bundle
              </h2>
              <p className="text-gray-500 mt-4 leading-relaxed">
                Every project below was delivered as our <strong className="text-[#1A1A1A]">Growth Bundle at S$3,999</strong> — the fastest, most affordable way for a Singapore SME to launch a brand that ranks and converts.
              </p>
            </div>
            <div className="bg-white rounded-3xl p-8 border border-gray-100">
              <div className="text-sm font-mono text-gray-400 uppercase tracking-widest mb-2">Delivered</div>
              <ul className="space-y-3 mb-6">
                {study.services.map((s) => (
                  <li key={s} className="flex items-start gap-2 text-[#1A1A1A]">
                    <Check size={18} className="text-[#C8102E] mt-0.5 flex-shrink-0" /> {s}
                  </li>
                ))}
              </ul>
              <div className="pt-5 border-t border-gray-100 flex items-end justify-between">
                <div>
                  <p className="text-xs font-mono text-gray-400 uppercase tracking-widest">Growth Bundle</p>
                  <p className="text-3xl font-bold text-[#1A1A1A] tracking-tight mt-1">S$3,999</p>
                </div>
                <Link to="/contact">
                  <Button className="bg-[#1A1A1A] hover:bg-[#C8102E] text-white rounded-full px-5 py-4 text-sm">
                    Get this bundle <ArrowRight size={14} className="ml-2" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem / Solution / Outcome */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
            {[
              { label: 'Problem', body: study.problem, accent: 'text-[#C8102E]' },
              { label: 'Solution', body: study.solution, accent: 'text-[#1A1A1A]' },
              { label: 'Outcome', body: null, accent: 'text-[#1A1A1A]' },
            ].map((block, i) => (
              <motion.div
                key={block.label}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.3 }}
                transition={{ duration: 0.6, delay: i * 0.1 }}
              >
                <span className={`font-mono text-xs uppercase tracking-[0.28em] ${block.accent} block mb-3`}>
                  0{i + 1} · {block.label}
                </span>
                {block.body ? (
                  <p className="text-lg text-gray-700 leading-[1.7]">{block.body}</p>
                ) : (
                  <div className="space-y-5">
                    {study.outcome.map((o) => (
                      <div key={o.label} className="border-l-2 border-[#C8102E] pl-4">
                        <p className="text-3xl font-bold text-[#1A1A1A] tracking-tight">{o.metric}</p>
                        <p className="text-sm text-gray-500 mt-1 leading-snug">{o.label}</p>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Tech + Keywords ranked */}
      <section className="py-16 bg-[#FAFAFA] border-y border-gray-100">
        <div className="container mx-auto px-6">
          <div className="max-w-5xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-10">
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.28em] text-gray-400 block mb-4">
                ~/stack
              </span>
              <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">Under the hood</h3>
              <div className="flex flex-wrap gap-2">
                {study.techStack.map((t) => (
                  <span
                    key={t}
                    className="font-mono text-xs text-gray-600 bg-white border border-gray-200 rounded-full px-3 py-1.5"
                  >
                    {t}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <span className="font-mono text-xs uppercase tracking-[0.28em] text-[#C8102E] block mb-4">
                ~/ranking
              </span>
              <h3 className="text-2xl font-bold text-[#1A1A1A] mb-6">Keywords we rank for</h3>
              <ul className="space-y-2">
                {study.keywords.map((k) => (
                  <li key={k} className="flex items-start gap-2 text-[#1A1A1A]">
                    <span className="text-[#C8102E] font-mono">▸</span>
                    <span className="font-medium">{k}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Other case studies */}
      {otherStudies.length > 0 && (
        <section className="py-20">
          <div className="container mx-auto px-6">
            <div className="max-w-6xl mx-auto">
              <div className="flex items-end justify-between mb-10">
                <div>
                  <span className="font-mono text-sm text-[#C8102E] tracking-tight">~/more-work</span>
                  <h2 className="text-3xl md:text-4xl font-bold text-[#1A1A1A] mt-2 tracking-tight">
                    Recent projects
                  </h2>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {otherStudies.map((o) => (
                  <Link
                    key={o.slug}
                    to={`/case-studies/${o.slug}`}
                    className="group block"
                    data-testid={`related-case-${o.slug}`}
                  >
                    <div className="relative overflow-hidden rounded-3xl bg-[#FAFAFA] aspect-[4/3] mb-4">
                      <img
                        src={o.thumbnail}
                        alt={`${o.title} — ${o.industry} case study by Hei Web Creations`}
                        loading="lazy"
                        className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-700"
                      />
                    </div>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-lg font-bold text-[#1A1A1A] group-hover:text-[#C8102E] transition-colors">
                          {o.title}
                        </h3>
                        <p className="text-sm text-gray-500 mt-1">{o.industry}</p>
                      </div>
                      <ArrowUpRight size={18} className="text-gray-400 group-hover:text-[#C8102E] transition-colors mt-1" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 bg-white" data-testid="case-cta">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto bg-[#1A1A1A] rounded-[2.5rem] px-8 py-16 md:px-16 relative overflow-hidden text-center">
            <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-[#C8102E]/25 blur-[120px] pointer-events-none" />
            <div className="relative z-10">
              <span className="inline-block font-mono text-xs uppercase tracking-[0.3em] text-[#C8102E] mb-4">
                ~/your-turn
              </span>
              <h2 className="text-3xl md:text-5xl font-bold text-white mb-5 tracking-tight leading-[1.05]">
                Ready to launch?
              </h2>
              <p className="text-lg text-gray-400 mb-10 max-w-2xl mx-auto leading-relaxed">
                Logo, website, SEO, and social — shipped in weeks for a flat <strong className="text-white">S$3,999</strong>.
              </p>
              <Link to="/contact">
                <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.97 }} className="inline-block">
                  <Button
                    className="bg-[#C8102E] hover:bg-white hover:text-[#1A1A1A] text-white px-10 py-6 text-base rounded-full transition-all duration-300"
                    data-testid="case-cta-primary"
                  >
                    Book Your Growth Bundle <ArrowRight size={18} className="ml-2" />
                  </Button>
                </motion.div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </article>
  );
};

export default CaseStudy;
